import { useState, useEffect, useCallback } from "react";
import { Row, Col, Tree, Typography, Divider, Space, message } from 'antd';
import difference from "lodash/difference";
import { getLangList } from '@/lib/i18n';
import PermissionConfig from "@/config/permission";
import { usePrevious } from '@/hooks';
import { ReButton } from "@/components/refineElement";
import { hasPermission } from "@/lib/permissionUtil";
import { RoleType, PermissionType } from './types';

const { getPermissionTreeInfo, getRolePermissionInfo, updateRolePermissionInfo } = PermissionConfig;
const { Title } = Typography;

type Props = {
  selectedRoleInfo: RoleType;
}

function PermissionTree(props: Props) {
  const { selectedRoleInfo } = props;
  const [permissionTreeArray, setPermissionTreeArray] = useState([]);
  const [rolePermissionArray, setRolePermissionArray] = useState<string[]>([]);
  const prevSelectedRoleInfo = usePrevious(selectedRoleInfo) ?? {};
  const { roleId: prevRoleId } = prevSelectedRoleInfo;
  const { roleDesc = '', roleId } = selectedRoleInfo;
  const organizationT: any = getLangList("organization");
  const buttonT: any = getLangList("button");
  const errorT: any = getLangList("error");

  const getFormatTreeData = useCallback((dataInfo: any) => {
    const formatArray = dataInfo?.map((item: PermissionType) => {
      const { id, permissionDesc, subPermissionList } = item;
      const children = getFormatTreeData(subPermissionList);
      return {
        ...item,
        key: `${id}`,
        title: permissionDesc,
        children,
      };
    }) ?? [];
    return formatArray;
  }, []);

  const getPermissionTreeInfoFunction = useCallback(() => {
    getPermissionTreeInfo().then((res: any) => {
      const permissionTreeArray = res?.value ?? [];
      const formatArray = getFormatTreeData(permissionTreeArray);
      setPermissionTreeArray(formatArray);
    });
  }, [getFormatTreeData]);

  const getRolePermissionInfoFunction = useCallback(() => {
    getRolePermissionInfo({ roleId }).then((res: any) => {
      const rolePermissionArray = res?.value?.map((item: number) => `${item}`) ?? [];
      setRolePermissionArray(rolePermissionArray);
    });
  }, [roleId]);

  function updateRolePermissionInfoFunction() {
    const params = { roleId, permissionIds: rolePermissionArray };

    updateRolePermissionInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
    });
  };

  const onCheck = (checkedKeys: any, e: any) => {
    const { checked } = checkedKeys;
    setRolePermissionArray(checked);
  };

  function getRootPermissionArray(dataInfo: any, idArray: any) {
    const formatArray: any = idArray;

    dataInfo?.forEach((item: any) => {
      const { key, children = [] } = item;
      formatArray.push(key);
      children.length > 0 && getRootPermissionArray(children, formatArray);
    });

    return formatArray;
  }

  const onRightClick = (e: any) => {
    const { node } = e;
    const { checked, children, key } = node;
    const formatArray = getRootPermissionArray(children, [key]);
    if (checked) {
      setRolePermissionArray(difference(rolePermissionArray, formatArray));
    } else {
      setRolePermissionArray([...rolePermissionArray, ...formatArray]);
    }
  };

  useEffect(() => {
    getPermissionTreeInfoFunction();
  }, [getPermissionTreeInfoFunction]);

  useEffect(() => {
    if (prevRoleId !== roleId) {
      getRolePermissionInfoFunction();
    }
  }, [prevRoleId, roleId, getRolePermissionInfoFunction]);

  return (
    <Row gutter={[0, 12]} justify="end">
      <Col span={24}>
        <Title level={4}>
          {`${buttonT.SETTING}${organizationT.PERMISSION}`}
          {roleDesc && ` - ${roleDesc}`}
        </Title>

        <Divider />
      </Col>

      <Col span={24}>
        <Tree
          checkable
          checkStrictly
          treeData={permissionTreeArray}
          checkedKeys={rolePermissionArray}
          onCheck={onCheck}
          onRightClick={onRightClick}
          disabled={!roleId}
        />
      </Col>

      {roleId && hasPermission('ORGANIZATION_PERMISSION_MANAGEMENT_EDIT') &&
        <Col>
          <Space>
            <ReButton onClick={getRolePermissionInfoFunction} attributeObject={{ type: 'default' }}>
              {buttonT.CANCEL}
            </ReButton>

            <ReButton onClick={updateRolePermissionInfoFunction}>
              {buttonT.OK}
            </ReButton>
          </Space>
        </Col>
      }
    </Row>
  );
}

export default PermissionTree;